import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Accessible horizontal scroll carousel with dot pagination and prev/next controls.
 * Desktop: grid layout (controls hidden). Mobile: scroll-snap carousel.
 *
 * @extends {Component<{ track: HTMLElement; prev: HTMLButtonElement; next: HTMLButtonElement; dots: HTMLElement; liveRegion?: HTMLElement }>}
 */
class KilwinsHscroll extends Component {
  requiredRefs = ['track', 'prev', 'next', 'dots'];

  #slides = [];
  #activeIndex = 0;
  #lastAnnouncedIndex = -1;
  #liveRegion;
  #resizeObserver;
  #mediaQuery = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();
    this.#slides = [...this.refs.track.children].filter((node) => node instanceof HTMLElement);
    this.#liveRegion = this.#resolveLiveRegion();
    this.#buildDots();
    this.#updateState();

    this.refs.track.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.refs.prev.addEventListener('click', this.#goPrev);
    this.refs.next.addEventListener('click', this.#goNext);
    this.refs.prev.addEventListener('keydown', this.#handleControlKeydown);
    this.refs.next.addEventListener('keydown', this.#handleControlKeydown);

    this.#mediaQuery.addEventListener('change', this.#handleResize);
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.refs.track);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track.removeEventListener('scroll', this.#handleScroll);
    this.refs.prev.removeEventListener('click', this.#goPrev);
    this.refs.next.removeEventListener('click', this.#goNext);
    this.refs.prev.removeEventListener('keydown', this.#handleControlKeydown);
    this.refs.next.removeEventListener('keydown', this.#handleControlKeydown);
    this.#mediaQuery.removeEventListener('change', this.#handleResize);
    this.#resizeObserver?.disconnect();
  }

  #buildDots() {
    const { dots } = this.refs;
    dots.replaceChildren();

    this.#slides.forEach((_, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'kilwins-hscroll__dot';
      button.setAttribute('aria-label', `Go to slide ${index + 1}`);
      button.addEventListener('click', () => this.#goTo(index));
      dots.append(button);
    });
  }

  #handleScroll = () => {
    if (!this.#mediaQuery.matches) return;

    const previousIndex = this.#activeIndex;
    this.#activeIndex = this.#getNearestIndex();
    this.#updateDots();
    this.#updateButtons();
    this.#updateScrollbar();
    this.#updateSlideFocus();

    if (previousIndex !== this.#activeIndex) {
      this.#announceSlideChange(this.#activeIndex);
    }
  };

  #handleResize = () => {
    this.#updateState();
  };

  #handleControlKeydown = (event) => {
    if (!this.#mediaQuery.matches) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#goPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#goNext();
    }
  };

  #goPrev = () => {
    this.#goTo(Math.max(0, this.#activeIndex - 1));
  };

  #goNext = () => {
    this.#goTo(Math.min(this.#slides.length - 1, this.#activeIndex + 1));
  };

  #goTo(index) {
    const slide = this.#slides[index];
    const { track } = this.refs;
    if (!slide) return;

    this.#activeIndex = index;
    const inset = this.#getScrollInset();
    track.scrollTo({
      left: Math.max(0, slide.offsetLeft - inset),
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
    this.#updateDots();
    this.#updateButtons();
    this.#updateSlideFocus();
    this.#announceSlideChange(index);
  }

  #resolveLiveRegion() {
    const { liveRegion } = this.refs;
    if (liveRegion instanceof HTMLElement) return liveRegion;

    const region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    this.appendChild(region);
    return region;
  }

  /**
   * Announces the active slide to screen readers in carousel mode.
   * @param {number} index - The 0-based slide index.
   */
  #announceSlideChange(index) {
    if (!this.#mediaQuery.matches) return;
    if (index === this.#lastAnnouncedIndex) return;

    const liveRegion = this.#liveRegion;
    if (!(liveRegion instanceof HTMLElement)) return;

    const slideCount = this.#slides.length;
    if (slideCount <= 0) return;

    const slide = this.#slides[index];
    const productTitle = slide?.querySelector('[data-product-title]')?.getAttribute('data-product-title');
    const message = productTitle
      ? this.#formatTranslation(Theme.translations?.carousel_product_viewing, {
          product: productTitle,
          index: `${index + 1}`,
          length: `${slideCount}`,
        })
      : this.#formatTranslation(Theme.translations?.slide_status, {
          index: `${index + 1}`,
          length: `${slideCount}`,
        });

    if (!message) return;

    this.#lastAnnouncedIndex = index;
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }

  /**
   * @param {string | undefined} template
   * @param {Record<string, string>} values
   * @returns {string | undefined}
   */
  #formatTranslation(template, values) {
    if (!template) return undefined;

    return Object.entries(values).reduce(
      (message, [key, value]) => message.replace(`[${key}]`, value),
      template,
    );
  }

  #getScrollInset() {
    const style = getComputedStyle(this.refs.track);
    const scrollInset =
      parseFloat(style.scrollPaddingInlineStart) || parseFloat(style.scrollPaddingLeft);
    if (scrollInset) return scrollInset;
    return parseFloat(style.paddingInlineStart) || parseFloat(style.paddingLeft) || 0;
  }

  #getNearestIndex() {
    const { track } = this.refs;
    const trackLeft = track.getBoundingClientRect().left;
    let nearest = 0;
    let nearestDistance = Infinity;

    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = index;
      }
    });

    return nearest;
  }

  #updateState() {
    const isMobile = this.#mediaQuery.matches;
    this.classList.toggle('kilwins-hscroll--mobile', isMobile);

    if (isMobile) {
      this.#activeIndex = this.#getNearestIndex();
    } else {
      this.#lastAnnouncedIndex = -1;
    }

    this.#updateDots();
    this.#updateButtons();
    this.#updateScrollbar();
    this.#updateSlideFocus();
  }

  /**
   * WCAG 2.4.3 — in carousel mode, only the active slide is in tab order.
   * Off-screen slides are inert (same pattern as slideshow.js).
   */
  #updateSlideFocus() {
    const isCarousel = this.#mediaQuery.matches;

    this.#slides.forEach((slide, index) => {
      if (!isCarousel) {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
        return;
      }

      const isActive = index === this.#activeIndex;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });
  }

  #updateScrollbar() {
    const thumb = this.refs.scrollbarThumb;
    const { track } = this.refs;
    if (!(thumb instanceof HTMLElement) || !this.#mediaQuery.matches) return;

    const scrollbarTrack = thumb.parentElement;
    if (!scrollbarTrack) return;

    const { scrollWidth, clientWidth, scrollLeft } = track;
    const scrollable = scrollWidth - clientWidth;

    if (scrollable <= 0) {
      thumb.style.width = '100%';
      thumb.style.transform = 'translateX(0)';
      return;
    }

    const trackWidth = scrollbarTrack.clientWidth;
    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 24);
    const maxOffset = trackWidth - thumbWidth;
    const offset = (scrollLeft / scrollable) * maxOffset;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${offset}px)`;
  }

  #updateDots() {
    const dotButtons = this.refs.dots.querySelectorAll('.kilwins-hscroll__dot');
    dotButtons.forEach((dot, index) => {
      const isActive = index === this.#activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  #updateButtons() {
    const { prev, next } = this.refs;
    prev.disabled = this.#activeIndex <= 0;
    next.disabled = this.#activeIndex >= this.#slides.length - 1;
  }
}

if (!customElements.get('kilwins-hscroll')) {
  customElements.define('kilwins-hscroll', KilwinsHscroll);
}
